<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"

import ScannerModal from "@/components/ScannerModal.vue"
import SlideoverFooter from "@/components/SlideoverFooter.vue"
import { barcodeScannerSupported } from "@/composables/useBarcodeScanner"
import { ICON } from "@/lib/icons"
import { containerPath, containersPath } from "@/routes"
import type { ContainerFormData, SelectOption } from "@/types/models"

const props = defineProps<{
  open: boolean
  container: ContainerFormData | null
  parentContainers: SelectOption[]
  itemTypes: SelectOption[]
  defaultParentId?: string
}>()

const emit = defineEmits<{
  "update:open": [value: boolean]
}>()

const name = ref("")
const parentId = ref<string | undefined>(undefined)
const itemTypeId = ref<string | undefined>(undefined)
const assetCode = ref("")
const assignedLocation = ref("")
const accessMethod = ref<string | undefined>(undefined)
const accessNotes = ref("")
const saving = ref(false)
const formErrors = ref<Record<string, string>>({})
const scannerOpen = ref(false)
const scannerSupported = barcodeScannerSupported

const isEdit = computed(() => !!props.container)
const title = computed(() => (isEdit.value ? "Edit Container" : "New Container"))
const submitLabel = computed(() => (isEdit.value ? "Save" : "Create Container"))

const accessMethodOptions = [
  { label: "None", value: "none" },
  { label: "Key", value: "key" },
  { label: "Combination", value: "combination" },
  { label: "Other", value: "other" },
]

const showAccessNotes = computed(() => accessMethod.value && accessMethod.value !== "none")

watch(
  () => props.open,
  (open) => {
    if (open) {
      name.value = props.container?.name ?? ""
      parentId.value = props.container?.parent_id ?? props.defaultParentId ?? undefined
      itemTypeId.value = props.container?.item_type_id ?? undefined
      assetCode.value = props.container?.asset_code ?? ""
      assignedLocation.value = props.container?.assigned_location ?? ""
      accessMethod.value = props.container?.access_method ?? undefined
      accessNotes.value = props.container?.access_notes ?? ""
      formErrors.value = {}
    }
  },
)

function close() {
  emit("update:open", false)
}

function submit() {
  saving.value = true
  const data = {
    container: {
      name: name.value,
      parent_id: parentId.value || null,
      item_type_id: itemTypeId.value || null,
      asset_code: assetCode.value || null,
      assigned_location: assignedLocation.value || null,
      access_method: accessMethod.value === "none" ? null : accessMethod.value || null,
      access_notes: showAccessNotes.value ? accessNotes.value || null : null,
    },
  }

  if (isEdit.value) {
    router.patch(containerPath(props.container!.id), data, {
      onSuccess: () => close(),
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    })
  } else {
    router.post(containersPath(), data, {
      onSuccess: () => close(),
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    })
  }
}

function handleScan(code: string) {
  assetCode.value = code
  scannerOpen.value = false
}
</script>

<template>
  <USlideover :open="open" :title="title" @update:open="emit('update:open', $event)">
    <template #body>
      <form id="container-form" class="flex flex-col gap-6" @submit.prevent="submit">
        <UFormField label="Name" required :error="formErrors.name">
          <UInput v-model="name" placeholder="e.g. Workshop Shelf" autofocus class="w-full" />
        </UFormField>

        <UFormField label="Parent Container" :error="formErrors.parent_id">
          <USelectMenu
            v-model="parentId"
            :items="parentContainers"
            value-key="value"
            placeholder="None (top-level)"
            class="w-full"
          />
        </UFormField>

        <UFormField label="Type" :error="formErrors.item_type_id">
          <USelectMenu
            v-model="itemTypeId"
            :items="itemTypes"
            value-key="value"
            placeholder="None"
            class="w-full"
          />
        </UFormField>

        <UFormField label="Asset Code" :error="formErrors.asset_code">
          <UInput v-model="assetCode" class="w-full font-mono">
            <template v-if="scannerSupported" #trailing>
              <UButton
                :icon="ICON.scanQr"
                color="neutral"
                variant="link"
                size="xs"
                :padded="false"
                @click="
                  () => {
                    scannerOpen = true
                  }
                "
              />
            </template>
          </UInput>
        </UFormField>

        <USeparator />

        <UFormField label="Assigned Location" :error="formErrors.assigned_location">
          <UInput v-model="assignedLocation" placeholder="e.g. Basement, Garage" class="w-full" />
        </UFormField>

        <UFormField label="Access Method" :error="formErrors.access_method">
          <USelectMenu
            v-model="accessMethod"
            :items="accessMethodOptions"
            value-key="value"
            placeholder="None"
            class="w-full"
          />
        </UFormField>

        <UFormField v-if="showAccessNotes" label="Access Notes" :error="formErrors.access_notes">
          <UInput v-model="accessNotes" placeholder="e.g. Combination is 1234" class="w-full" />
        </UFormField>
      </form>
    </template>

    <template #footer>
      <SlideoverFooter
        form-id="container-form"
        :submit-label="submitLabel"
        :loading="saving"
        @cancel="close"
      />
    </template>
  </USlideover>

  <ScannerModal v-model:open="scannerOpen" @scan="handleScan" />
</template>
